import { useEffect, useRef, useState } from 'react'
import {
    ALLOWED_EMAIL_DOMAIN,
    GOOGLE_CLIENT_ID,
    GoogleIdentity,
    forgetGoogleAccount,
    identityFromCredential,
    isAllowedDomain,
    loadGoogleIdentityServices,
} from '../data/google'
import './css/login.css'

type GoogleSignInProperties = {
    onSignedIn(identity: GoogleIdentity): void;
}

// Renders the "Sign in with Google" button and only calls back for accounts on
// ALLOWED_EMAIL_DOMAIN. The check happens in the browser, see src/data/google.ts.
export default function GoogleSignIn(props: GoogleSignInProperties){
    const buttonRef = useRef<HTMLDivElement>(null)
    const mountedRef = useRef(true)
    const onSignedInRef = useRef(props.onSignedIn)
    const [feedbackText, setFeedbackText] = useState('')

    onSignedInRef.current = props.onSignedIn

    useEffect(() => {
        if(!GOOGLE_CLIENT_ID){
            setFeedbackText('Google sign-in is not configured: REACT_APP_GOOGLE_CLIENT_ID is missing.')
            return
        }

        const handleCredential = (response: any) => {
            if(!mountedRef.current)
                return
            try{
                const identity = identityFromCredential(response.credential)
                if(!isAllowedDomain(identity)){
                    // Forget the account, otherwise the next click signs in
                    // silently with the same one and nothing seems to happen.
                    forgetGoogleAccount()
                    setFeedbackText(
                        (identity.email ? identity.email : 'That account') +
                        ' is not a @' + ALLOWED_EMAIL_DOMAIN + ' account. Sign in with your Zabatt account to continue.'
                    )
                    return
                }
                setFeedbackText('')
                onSignedInRef.current(identity)
            }
            catch(err: any){
                console.log('Error', err)
                setFeedbackText('Could not read the Google sign-in response.')
            }
        }

        loadGoogleIdentityServices().then((google: any) => {
            if(!mountedRef.current)
                return
            google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: handleCredential,
                auto_select: false,
                cancel_on_tap_outside: true,
                // Filters the account chooser only, the domain is checked again
                // in handleCredential.
                hd: ALLOWED_EMAIL_DOMAIN,
            })
            google.accounts.id.renderButton(buttonRef.current, {
                type: 'standard',
                theme: 'outline',
                size: 'large',
                text: 'signin_with',
                shape: 'rectangular',
                logo_alignment: 'left',
                width: 260,
            })
        }).catch((err: any) => {
            console.log('Error', err)
            if(mountedRef.current)
                setFeedbackText('Could not load Google sign-in. Check your connection and try again.')
        })

        return () => {
            mountedRef.current = false
        }
    }, [])

    return (
        <div className="google-gate">
            <div ref={buttonRef} className="google-gate-button"></div>
            {feedbackText ? <p className="google-gate-feedback">{feedbackText}</p> : null}
        </div>
    )
}
